import React, { useState, useEffect } from "react";
import { UI_TEXT, ANIMATIONS } from "../constants";
import { useGameAudio } from "../GameAudioProvider";

interface Level {
  id: number;
  title?: string;
  subtitle?: string;
  description?: string;
  difficulty?: "Easy" | "Medium" | "Hard";
  isUnlocked: boolean;
  isCompleted: boolean;
  stars?: number;
  score?: number;
}

interface LevelSelectionScreenProps {
  totalLevels: number;
  currentLevel: number;
  userProgress: {
    unlockedLevel: number;
    completedLevels: number[];
    levelScores: Record<number, number>;
    levelStars: Record<number, number>;
  };
  onLevelSelect: (level: number) => void;
  gameTitle: string;
}

const LevelSelectionScreen: React.FC<LevelSelectionScreenProps> = ({
  totalLevels,
  currentLevel,
  userProgress,
  onLevelSelect,
  gameTitle,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { stopAudio } = useGameAudio();

  useEffect(() => {
    setIsLoaded(true);
    const params = new URLSearchParams(window.location.search);
    if (params.has("token")) {
      console.log("Admin token found, unlocking all levels.");
      setIsAdmin(true);
    }
  }, []);

  const handleLevelSelect = (levelId: number) => {
    stopAudio();
    onLevelSelect(levelId);
  };

  const generateLevels = (): Level[] => {
    return Array.from({ length: totalLevels }, (_, index) => {
      const levelNum = index + 1;
      return {
        id: levelNum,
        isUnlocked: isAdmin || levelNum <= userProgress.unlockedLevel,
        isCompleted: userProgress.completedLevels.includes(levelNum),
        stars: userProgress.levelStars[levelNum] || 0,
        score: userProgress.levelScores[levelNum] || 0,
      };
    });
  };

  const levels = generateLevels();
  const completionPercentage =
    (userProgress.completedLevels.length / totalLevels) * 100;

  const getLevelButtonClass = (level: Level) => {
    if (level.isCompleted) {
      return "relative w-full aspect-square rounded-2xl font-bold text-sm md:text-base border-4 transition-all duration-300 transform bg-gradient-to-br from-green-400 to-blue-500 text-white shadow-xl border-green-300 hover:scale-110 hover:shadow-2xl active:scale-95";
    }
    if (level.id === currentLevel) {
      return "relative w-full aspect-square rounded-2xl font-bold text-sm md:text-base border-4 transition-all duration-300 transform bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-2xl border-yellow-300 animate-pulse hover:scale-110 hover:shadow-2xl active:scale-95";
    }
    return "relative w-full aspect-square rounded-2xl font-bold text-sm md:text-base border-4 transition-all duration-300 transform bg-gradient-to-br from-blue-500 to-purple-600 text-white shadow-lg border-blue-300 hover:scale-105 hover:shadow-2xl active:scale-95";
  };

  const renderStars = (stars: number) => {
    return Array.from({ length: 3 }, (_, index) => (
      <span
        key={index}
        className={`text-xs ${
          index < stars ? "text-yellow-400" : "text-gray-300"
        }`}
      >
        ⭐
      </span>
    ));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        {Array.from({ length: 15 }, (_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full animate-float"
            style={{
              left: Math.random() * 100 + "%",
              top: Math.random() * 100 + "%",
              width: Math.random() * 20 + 10 + "px",
              height: Math.random() * 20 + 10 + "px",
              animationDelay: Math.random() * 3 + "s",
              animationDuration: Math.random() * 3 + 3 + "s",
            }}
          />
        ))}
      </div>

      <div
        className={`container mx-auto px-4 py-8 transform transition-all ${
          isLoaded ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
        }`}
        style={{ transitionDuration: `${ANIMATIONS.FADE_DURATION}ms` }}
      >
        <div className="text-center mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-white mb-4 drop-shadow-2xl">
            {UI_TEXT.CHOOSE_LEVEL}
          </h2>
          <p className="text-sm md:text-base text-white/90 mb-6">
            {gameTitle} - Progress your adventure!
          </p>

          <div className="max-w-md mx-auto mb-4">
            <div className="flex justify-between text-white/80 mb-2">
              <span>{UI_TEXT.PROGRESS}</span>
              <span>
                {userProgress.completedLevels.length}/{totalLevels}{" "}
                {UI_TEXT.LEVELS}
              </span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-400 to-blue-500 transition-all duration-1000 ease-out"
                style={{ width: completionPercentage + "%" }}
              />
            </div>
            <div className="text-center text-white/70 mt-2">
              {Math.round(completionPercentage)}% {UI_TEXT.COMPLETE}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {levels.map((level, index) => (
              <div
                key={level.id}
                className={`transform transition-all duration-300 delay-${
                  index * 50
                } ${
                  isLoaded
                    ? "translate-y-0 opacity-100"
                    : "translate-y-10 opacity-0"
                }`}
              >
                <button
                  onClick={() => level.isUnlocked && handleLevelSelect(level.id)}
                  disabled={!level.isUnlocked}
                  className={getLevelButtonClass(level)}
                >
                  {!level.isUnlocked && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg
                        className="w-8 h-8"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M18 10V6C18 3.79 16.21 2 14 2H10C7.79 2 6 3.79 6 6V10C4.9 10 4 10.9 4 12V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V12C20 10.9 19.1 10 18 10ZM8 6C8 4.9 8.9 4 10 4H14C15.1 4 16 4.9 16 6V10H8V6Z" />
                      </svg>
                    </div>
                  )}

                  {level.isUnlocked && (
                    <div className="flex flex-col items-center justify-center h-full">
                      <span className="text-sm md:text-base font-bold mb-1">
                        {level.id}
                      </span>

                      {level.isCompleted && (
                        <div className="flex space-x-1">
                          {renderStars(level.stars || 0)}
                        </div>
                      )}

                      {level.id === currentLevel && !level.isCompleted && (
                        <div className="absolute -top-2 -right-2 bg-yellow-400 rounded-full w-6 h-6 flex items-center justify-center">
                          <span className="text-xs font-bold text-yellow-900">
                            ▶
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {level.isCompleted && (
                    <div className="absolute -top-2 -right-2 bg-green-500 rounded-full w-8 h-8 flex items-center justify-center shadow-lg">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  )}
                </button>

                <div className="text-center mt-2">
                  <span className="text-white/80 text-xs font-medium">
                    Level {level.id}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 text-center">
          <div className="inline-flex space-x-8 bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">
                {userProgress.completedLevels.length}
              </div>
              <div className="text-white/80 text-sm font-medium">{UI_TEXT.COMPLETED}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">
                {Math.min(userProgress.unlockedLevel, totalLevels)}
              </div>
              <div className="text-white/80 text-sm font-medium">Available</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">
                {Object.values(userProgress.levelStars).reduce(
                  (sum, stars) => sum + stars,
                  0
                )}
              </div>
              <div className="text-white/80 text-sm font-medium">{UI_TEXT.STARS}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export type { LevelSelectionScreenProps, Level };
export { LevelSelectionScreen };
export default LevelSelectionScreen;